import type { Dayjs } from "dayjs";
import dayjs, { APP_TIMEZONE } from "./dayjsConfig";
import type { FormInstance } from "antd";

export const MIN_DATETIME_GAP_MINUTES = 30;
/** Latest scheduled publish is this many minutes before event start. */
export const PUBLISH_BEFORE_EVENT_START_MINUTES = 5;

const TIME_PARSE_FORMATS = [
  "hh:mm A",
  "h:mm A",
  "HH:mm:ss",
  "HH:mm",
] as const;

/** Current moment in Asia/Kolkata. */
export function getNow(): Dayjs {
  return dayjs().tz(APP_TIMEZONE).second(0).millisecond(0);
}

/** Start of today in Asia/Kolkata. */
export function getTodayStart(): Dayjs {
  return getNow().startOf("day");
}

export function isSameCalendarDay(
  a: string | Dayjs | undefined | null,
  b: string | Dayjs | undefined | null,
): boolean {
  if (!a || !b) return false;
  const da = dayjs(a);
  const db = dayjs(b);
  if (!da.isValid() || !db.isValid()) return false;
  return da.format("YYYY-MM-DD") === db.format("YYYY-MM-DD");
}

export function isToday(date: string | Dayjs | undefined | null): boolean {
  return isSameCalendarDay(date, getTodayStart());
}

export const disabledDate = (current: Dayjs): boolean => {
  return !!current && current.isBefore(getTodayStart(), "day");
};

export const disabledPastDate = (current: Dayjs): boolean => {
  return !!current && current.isAfter(getTodayStart(), "day");
};

export const disableFutureDates = (current: Dayjs) => {
  return !!current && current.isAfter(getNow().endOf("day"), "day");
};

export function parseTimeValue(
  time: string | Dayjs | undefined | null,
): Dayjs | null {
  if (!time) return null;
  if (dayjs.isDayjs(time)) return time.isValid() ? time : null;
  const parsed = dayjs(time, [...TIME_PARSE_FORMATS], true);
  return parsed.isValid() ? parsed : null;
}

/** Min/max datetimes for coupon/discount fields tied to event schedule. */
export function getEventDateTimeBounds(event?: {
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
}): DateTimeRangeBounds {
  const eventStart = combineDateAndTime(event?.start_date, event?.start_time);
  const eventEnd = combineDateAndTime(event?.end_date, event?.end_time);
  const now = getNow();

  let minDateTime: Dayjs | null = now;
  if (eventStart?.isValid() && eventStart.isAfter(now)) {
    minDateTime = eventStart;
  }

  return {
    minDateTime,
    maxDateTime: eventEnd?.isValid() ? eventEnd : null,
  };
}

/**
 * Coupon start/end: any date within the event (calendar), time not in the past.
 * Does not force start time to be after event start — only after now.
 */
export function getCouponDateTimeBounds(event?: {
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
}): DateTimeRangeBounds {
  const eventEnd = combineDateAndTime(event?.end_date, event?.end_time);
  const now = getNow();

  return {
    minDateTime: now,
    maxDateTime: eventEnd?.isValid() ? eventEnd : null,
  };
}

/** True when event runs on a single calendar day (start date equals end date). */
export function isEventSingleDay(event?: {
  start_date?: string;
  end_date?: string;
}): boolean {
  if (!event?.start_date || !event?.end_date) {
    return false;
  }

  return dayjs(event.start_date).isSame(dayjs(event.end_date), "day");
}

export function combineDateAndTime(
  date: string | Dayjs | undefined | null,
  time: string | Dayjs | undefined | null,
): Dayjs | null {
  if (!date) return null;
  const datePart = dayjs(date);
  if (!datePart.isValid()) return null;

  const timePart = parseTimeValue(time);
  if (!timePart) return null;

  return datePart
    .hour(timePart.hour())
    .minute(timePart.minute())
    .second(0)
    .millisecond(0);
}

/** Disable calendar days before today and optionally before minDate / after maxDate. */
export function getDisabledDateWithBounds(options?: {
  minDate?: Dayjs | string;
  maxDate?: Dayjs | string;
  /** When false, only minDate applies (used when editing an existing event schedule). */
  floorAtToday?: boolean;
}): (current: Dayjs | null) => boolean {
  const today = getTodayStart();
  const minBound = options?.minDate
    ? dayjs(options.minDate).startOf("day")
    : null;

  const effectiveMin =
    options?.floorAtToday === false && minBound?.isValid()
      ? minBound
      : minBound?.isValid()
        ? minBound.isAfter(today)
          ? minBound
          : today
        : today;

  const maxBound = options?.maxDate
    ? dayjs(options.maxDate).startOf("day")
    : null;

  return (current: Dayjs | null) => {
    if (!current) return false;
    if (current.isBefore(effectiveMin, "day")) return true;
    if (maxBound && current.isAfter(maxBound, "day")) return true;
    return false;
  };
};

export const getDisabledDateFn = (endDateStr: string) => {
  return getDisabledDateWithBounds({ maxDate: endDateStr });
};

export function getEarliestDate(dates: string[]): string {
  const validDates = dates
    .map((dateStr) => new Date(dateStr))
    .filter((d) => !isNaN(d.getTime()));

  const earliestDate = validDates.reduce((prev, curr) =>
    curr < prev ? curr : prev,
  );

  return earliestDate.toISOString().split("T")[0];
}

export function activeSpecificDate(startDate: Dayjs, endDate: Dayjs) {
  return getDisabledDateWithBounds({
    minDate: startDate,
    maxDate: endDate,
  });
}

export function validateTimeIsNotInPast(
  date: string | Dayjs,
  time: string | Dayjs,
): boolean {
  const combined = combineDateAndTime(date, time);
  if (!combined) return false;
  return combined.isBefore(getNow());
}

export type DateTimeRangeBounds = {
  minDateTime?: Dayjs | null;
  maxDateTime?: Dayjs | null;
  /** Original saved event start — used when editing so dates are not locked to "today" only. */
  savedStartDateTime?: Dayjs | null;
  isEdit?: boolean;
};

export type SavedEventSchedule = {
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
};

export function boundsFromSavedEventSchedule(
  schedule?: SavedEventSchedule,
): DateTimeRangeBounds | undefined {
  if (!schedule?.start_date) return undefined;

  const savedStart = combineDateAndTime(
    schedule.start_date,
    schedule.start_time,
  );
  const savedEnd = combineDateAndTime(schedule.end_date, schedule.end_time);

  return {
    minDateTime: savedStart,
    maxDateTime: savedEnd,
    savedStartDateTime: savedStart,
    isEdit: true,
  };
}

export type DateTimeRangeFieldNames = {
  startDate: string;
  startTime: string;
  endDate?: string;
  endTime?: string;
};

function timeOnDate(referenceDay: Dayjs, source: Dayjs): Dayjs {
  return referenceDay
    .hour(source.hour())
    .minute(source.minute())
    .second(0)
    .millisecond(0);
}

/** Round up to the next 5-minute slot (used when opening the time picker for today). */
export function roundUpToNextMinutes(base: Dayjs, step = 5): Dayjs {
  const totalMinutes = base.hour() * 60 + base.minute();
  const next = Math.ceil((totalMinutes + 1) / step) * step;
  return base.startOf("day").add(next, "minute");
}

/** Suggested time when opening the picker (never in the past for today). */
export function getDefaultSelectableTime(
  referenceDate: Dayjs,
  options?: { minTime?: Dayjs; maxTime?: Dayjs },
): Dayjs {
  const refDay = referenceDate.startOf("day");
  let candidate = isToday(refDay)
    ? roundUpToNextMinutes(getNow())
    : refDay.hour(9).minute(0);

  candidate = refDay
    .hour(candidate.hour())
    .minute(candidate.minute())
    .second(0);

  if (options?.minTime && candidate.isBefore(options.minTime)) {
    candidate = options.minTime;
  }
  if (options?.maxTime && candidate.isAfter(options.maxTime)) {
    candidate = options.maxTime;
  }

  return candidate;
}

export function getTimePickerBounds(options: {
  pairedDate: Dayjs | null | undefined;
  minDateTime?: Dayjs | null;
  maxDateTime?: Dayjs | null;
  savedStartDateTime?: Dayjs | null;
  /** When false (event edit), do not force "now" on today's calendar day. */
  enforceNowOnToday?: boolean;
  minFrom?: {
    date: Dayjs | null | undefined;
    time: string | Dayjs | null | undefined;
    gapMinutes: number;
  };
}): { minTime: Dayjs | undefined; maxTime: Dayjs | undefined } {
  const {
    pairedDate,
    minDateTime,
    maxDateTime,
    savedStartDateTime,
    enforceNowOnToday = true,
    minFrom,
  } = options;
  if (!pairedDate?.isValid()) {
    return { minTime: undefined, maxTime: undefined };
  }

  const refDay = pairedDate.startOf("day");
  const now = getNow();
  let minTime: Dayjs | undefined;
  let maxTime: Dayjs | undefined;

  if (
    savedStartDateTime?.isValid() &&
    isSameCalendarDay(pairedDate, savedStartDateTime)
  ) {
    const savedMin = timeOnDate(refDay, savedStartDateTime);
    minTime = savedMin;
  }

  if (enforceNowOnToday && isToday(refDay)) {
    const nowMin = timeOnDate(refDay, now);
    minTime = minTime
      ? nowMin.isAfter(minTime)
        ? nowMin
        : minTime
      : nowMin;
  }

  if (minDateTime?.isValid() && isSameCalendarDay(pairedDate, minDateTime)) {
    const boundMin = timeOnDate(refDay, minDateTime);
    minTime = minTime
      ? boundMin.isAfter(minTime)
        ? boundMin
        : minTime
      : boundMin;
  }

  if (maxDateTime?.isValid() && isSameCalendarDay(pairedDate, maxDateTime)) {
    maxTime = timeOnDate(refDay, maxDateTime);
  }

  if (minFrom?.date?.isValid() && isSameCalendarDay(pairedDate, minFrom.date)) {
    const start = combineDateAndTime(minFrom.date, minFrom.time);
    if (start) {
      const gapMin = timeOnDate(refDay, start.add(minFrom.gapMinutes, "minute"));
      minTime = minTime
        ? gapMin.isAfter(minTime)
          ? gapMin
          : minTime
        : gapMin;
    }
  }

  if (minTime && maxTime && minTime.isAfter(maxTime)) {
    return { minTime: undefined, maxTime: undefined };
  }

  return { minTime, maxTime };
}

export function createDateTimeRangeValidator(
  form: FormInstance,
  fields: DateTimeRangeFieldNames,
  options?: {
    minGapMinutes?: number;
    bounds?: DateTimeRangeBounds;
    requireEnd?: boolean;
    maxDateTimeErrorMessage?: string | ((max: Dayjs) => string);
  },
) {
  const gap = options?.minGapMinutes ?? MIN_DATETIME_GAP_MINUTES;
  const { minDateTime, maxDateTime, savedStartDateTime, isEdit } =
    options?.bounds ?? {};
  const now = getNow();

  return async () => {
    const start = combineDateAndTime(
      form.getFieldValue(fields.startDate),
      form.getFieldValue(fields.startTime),
    );

    const hasEnd = Boolean(fields.endDate && fields.endTime);
    const end =
      hasEnd &&
      combineDateAndTime(
        form.getFieldValue(fields.endDate!),
        form.getFieldValue(fields.endTime!),
      );

    if (!start && (!options?.requireEnd || !hasEnd)) {
      return Promise.resolve();
    }

    if (start) {
      if (
        isEdit &&
        savedStartDateTime?.isValid() &&
        start.isBefore(savedStartDateTime)
      ) {
        return Promise.reject(
          new Error(
            "Start cannot be earlier than the previously saved event start",
          ),
        );
      }
      if (!isEdit && start.isBefore(now)) {
        return Promise.reject(
          new Error("Start date and time cannot be in the past"),
        );
      }
      if (
        isEdit &&
        !savedStartDateTime?.isValid() &&
        start.isBefore(now)
      ) {
        return Promise.reject(
          new Error("Start date and time cannot be in the past"),
        );
      }
      if (minDateTime && start.isBefore(minDateTime)) {
        return Promise.reject(
          new Error("Start date and time is before the allowed range"),
        );
      }
      if (maxDateTime && start.isAfter(maxDateTime)) {
        const message =
          typeof options?.maxDateTimeErrorMessage === "function"
            ? options.maxDateTimeErrorMessage(maxDateTime)
            : (options?.maxDateTimeErrorMessage ??
              `Start cannot be after event end (${maxDateTime.format("hh:mm A")})`);
        return Promise.reject(new Error(message));
      }
      if (start.isAfter(dayjs().add(1, "year"))) {
        return Promise.reject(
          new Error("Start date cannot be more than 1 year from today"),
        );
      }
    }

    if (end) {
      if (
        isEdit &&
        savedStartDateTime?.isValid() &&
        end.isBefore(savedStartDateTime)
      ) {
        return Promise.reject(
          new Error(
            "End cannot be earlier than the previously saved event start",
          ),
        );
      }
      if (!isEdit && end.isBefore(now)) {
        return Promise.reject(
          new Error("End date and time cannot be in the past"),
        );
      }
      if (minDateTime && end.isBefore(minDateTime)) {
        return Promise.reject(
          new Error("End date and time is before the allowed range"),
        );
      }
      if (maxDateTime && end.isAfter(maxDateTime)) {
        return Promise.reject(
          new Error(
            `End cannot be after event end (${maxDateTime.format("hh:mm A")})`,
          ),
        );
      }
    }

    if (start && end) {
      if (!end.isAfter(start)) {
        return Promise.reject(
          new Error(
            gap === 0
              ? "Sales close must be after sales open"
              : "End date and time must be after start date and time",
          ),
        );
      }
      if (
        gap > 0 &&
        isSameCalendarDay(start, end) &&
        end.diff(start, "minute") < gap
      ) {
        return Promise.reject(
          new Error(
            `End time must be at least ${gap} minutes after start time on the same day`,
          ),
        );
      }
    }

    return Promise.resolve();
  };
}

export function boundsFromEventWindow(
  startDate?: string,
  startTime?: string,
  endDate?: string,
  endTime?: string,
): DateTimeRangeBounds {
  return {
    minDateTime: combineDateAndTime(startDate, startTime) ?? getNow(),
    maxDateTime: combineDateAndTime(endDate, endTime) ?? null,
  };
}

/** Publish / schedule: from now until 5 minutes before event start. */
export function getPublishDateTimeBounds(event?: {
  start_date?: string;
  start_time?: string;
}): DateTimeRangeBounds {
  const eventStart = combineDateAndTime(event?.start_date, event?.start_time);
  const maxDateTime =
    eventStart?.isValid()
      ? eventStart.subtract(PUBLISH_BEFORE_EVENT_START_MINUTES, "minute")
      : null;

  return {
    minDateTime: getNow(),
    maxDateTime,
  };
}

export function publishMaxDateTimeError(maxDateTime: Dayjs): string {
  return `Publish date and time must be at least ${PUBLISH_BEFORE_EVENT_START_MINUTES} minutes before event start (latest: ${maxDateTime.format("ddd, D MMM YYYY, h:mm A")})`;
}
